import { post, get, put, patch, remove } from "../../ApiService";
import { ApiUrl } from "../../ApiUrl";
import { GetEquipmentListParams, GetEquipmentUsageListParams } from "./types";

export const listEquipmentUsage = async (
  params: GetEquipmentUsageListParams,
  region: number
) => {
  try {
    const queryParams = new URLSearchParams();

    const isFiltered = !!params.year || !!params.month;
    if (!isFiltered && params.page) queryParams.append("page", params.page.toString());
    if (!isFiltered && params.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params.orderBy)
      queryParams.append("order_by", params.orderBy.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.year) queryParams.append("year", params.year);
    if (params.month) queryParams.append("month", params.month);
    if (params.site) queryParams.append("site", params.site);

    const queryString = queryParams.toString();
    const url = `${ApiUrl.equipmentModule.equipmentMonthlyUsage}${
      queryString ? `?${queryString}` : ""
    }&region=${region}&project=${params.project}&site=${params.site}`;

    const res = await get(url);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const getEquipmentUsageById = async (id: number, project: any) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentMonthlyUsageById(
      id
    )}?project=${project}`;
    const res = await get(url);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const createEquipmentUsage = async (
  data: any,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentMonthlyUsage}?region=${region}&project=${project}`;
    const res = await post(url, data);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const updateEquipmentUsage = async (
  id: number,
  data: any,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentMonthlyUsageById(
      id
    )}?region=${region}&project=${project}`;
    const res = await put(url, data);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const deleteEquipmentUsage = async (
  id: number,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentMonthlyUsageById(
      id
    )}?region=${region}&project=${project}`;
    const res = await remove(url);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const uploadEquipmentUsageAttachment = async (
  id: number,
  data: FormData,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentUsageAttachment(
      id
    )}?region=${region}&project=${project}`;
    const res = await post(url, data);
    return res;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || "File upload failed",
      data: error.response,
    };
  }
};

export const getEquipmentUsageFiles = async (
  id: number,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentUsageAttachment(
      id
    )}?region=${region}&project=${project}`;
    const res = await get(url);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const getEquipmentUsageFile = async (
  fileId: number,
  equipmentUsageId: number,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentUsageAttachment(
      equipmentUsageId
    )}${fileId}?region=${region}&project=${project}`;
    const res = await get(url);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const deleteEquipmentUsageFile = async (
  fileId: number,
  equipmentUsageId: number,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentUsageAttachment(
      equipmentUsageId
    )}${fileId}/?region=${region}&project=${project}`;
    const res = await remove(url);
    return res;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || "File delete failed",
      data: error.response,
    };
  }
};

export const updateEquipmentUsageAttachment = async (
  fileId: number,
  equipmentUsageId: number,
  data: FormData,
  region: number,
  project: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.equipmentUsageAttachment(
      equipmentUsageId
    )}${fileId}/?region=${region}&project=${project}`;
    const res = await patch(url, data);
    return res;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || "File update failed",
      data: error.response,
    };
  }
};

export const downloadEquipmentUsageReports = async (
  params: GetEquipmentUsageListParams,
  region: number,
  project: any
) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.orderBy)
      queryParams.append("order_by", params.orderBy.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.year) queryParams.append("year", params.year);
    if (params.month) queryParams.append("month", params.month);

    const queryString = queryParams.toString();
    const url = `${ApiUrl.equipmentModule.downloadEquipmentUsageReports}${
      queryString ? `?${queryString}` : ""
    }&region=${region}&project=${project}&site=${params.site}`;

    const response = await get(url);

    if (response.status === 200) {
      // Get the filename from the Content-Disposition header, or use a default name
      const contentDisposition = response.headers["content-disposition"];
      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1].replace(/["']/g, "")
        : "offroad-report.csv";

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "text/csv" });

      // Create a temporary link element
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = filename;

      // Append to the document, click it, and remove it
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Clean up the URL object
      window.URL.revokeObjectURL(downloadLink.href);

      return { success: true };
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error: any) {
    return error.response || error;
  }
};

export const uploadEquipmentUsage = async (
  data: FormData,
  region: number,
  project: any,
  site: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.uploadEquipmentUsage}?region=${region}&project=${project}&site=${site}`;
    const res = await post(url, data);
    return res;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || "File upload failed",
      data: error.response,
    };
  }
};

export const uploadEquipment = async (
  data: FormData,
  region: number,
  project: any,
  site: any
) => {
  try {
    const url = `${ApiUrl.equipmentModule.uploadEquipment}?region=${region}&project=${project}&site=${site}`;
    const res = await post(url, data);
    return res;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || "File upload failed",
      data: error.response,
    };
  }
};
